import React, { useRef, useEffect, useState } from 'react';

const LetsGetStartedSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          setIsPlaying(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative h-[60vh] min-h-[400px] overflow-hidden bg-black"
    >
      {/* Video Background with Neon Effects */}
      <div className="absolute inset-0">
        {/* Animated Neon Overlay */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-red-500/20 rounded-full filter blur-3xl animate-pulse" />
          <div className="absolute top-1/4 right-0 w-80 h-80 bg-green-500/20 rounded-full filter blur-3xl animate-pulse delay-1000" />
          <div className="absolute bottom-1/4 left-1/4 w-72 h-72 bg-yellow-500/20 rounded-full filter blur-3xl animate-pulse delay-500" />
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-blue-500/20 rounded-full filter blur-3xl animate-pulse delay-1500" />
        </div>

        {/* Animated Lines */}
        <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="redGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#ef4444" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#dc2626" stopOpacity="0.2" />
            </linearGradient>
            <linearGradient id="greenGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#22c55e" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#16a34a" stopOpacity="0.2" />
            </linearGradient>
            <linearGradient id="yellowGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#eab308" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#ca8a04" stopOpacity="0.2" />
            </linearGradient>
            <linearGradient id="blueGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#2563eb" stopOpacity="0.2" />
            </linearGradient>
          </defs>
          
          {/* Animated Neon Lines */}
          <path
            d="M 0 200 Q 200 100 400 200 T 800 200"
            stroke="url(#redGradient)"
            strokeWidth="2"
            fill="none"
            className={`transition-all duration-3000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
            style={{
              animation: 'drawLine 3s ease-in-out infinite',
              filter: 'drop-shadow(0 0 10px rgba(239, 68, 68, 0.8))'
            }}
          />
          <path
            d="M 100 0 Q 300 150 500 50 T 900 150"
            stroke="url(#greenGradient)"
            strokeWidth="2"
            fill="none"
            className={`transition-all duration-3000 delay-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
            style={{
              animation: 'drawLine 3s ease-in-out infinite 0.5s',
              filter: 'drop-shadow(0 0 10px rgba(34, 197, 94, 0.8))'
            }}
          />
          <path
            d="M 0 400 Q 250 300 500 400 T 1000 350"
            stroke="url(#yellowGradient)"
            strokeWidth="2"
            fill="none"
            className={`transition-all duration-3000 delay-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
            style={{
              animation: 'drawLine 3s ease-in-out infinite 1s',
              filter: 'drop-shadow(0 0 10px rgba(234, 179, 8, 0.8))'
            }}
          />
          <path
            d="M 200 500 Q 400 350 600 450 T 1100 400"
            stroke="url(#blueGradient)"
            strokeWidth="2"
            fill="none"
            className={`transition-all duration-3000 delay-1500 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
            style={{
              animation: 'drawLine 3s ease-in-out infinite 1.5s',
              filter: 'drop-shadow(0 0 10px rgba(59, 130, 246, 0.8))'
            }}
          />
        </svg>

        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/60" />
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex items-center px-6 md:px-12 lg:px-24">
        <div className="max-w-2xl">
          {/* Partner With Us */}
          <div
            className={`overflow-hidden mb-6 transition-all duration-1000 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}
          >
            <p className="text-white/80 text-sm md:text-base tracking-[0.3em] uppercase font-spectral">
              PARTNER WITH US
            </p>
          </div>

          {/* Let's Get Started */}
          <div
            className={`overflow-hidden mb-12 transition-all duration-1000 delay-300 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'
            }`}
          >
            <h2
              className="text-white text-3xl md:text-5xl lg:text-6xl font-spectral tracking-wide font-spectral"
              style={{ fontFamily: "'Spectral SC', serif" }}
            >
              LETS GET
              <br />
              STARTED
            </h2>
          </div>

          {/* CTA Button */}
          <div
            className={`overflow-hidden transition-all duration-1000 delay-700 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}
          >
            <button
              className="group relative px-8 py-4 border border-white text-white font-medium tracking-[0.1em] uppercase text-sm transition-all duration-300 hover:bg-white hover:text-black"
              onClick={() => {
                // Handle CTA click - could open contact form, email, etc.
                window.location.href = 'mailto:contact@brewingframes.com';
              }}
            >
              <span className="relative z-10">SHARE YOUR BRIEF</span>
              
              {/* Hover effect background */}
              <div className="absolute inset-0 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
            </button>
          </div>
        </div>
      </div>

      {/* Custom Styles */}
      <style jsx>{`
        @keyframes drawLine {
          0%, 100% {
            stroke-dasharray: 0 1000;
          }
          50% {
            stroke-dasharray: 1000 0;
          }
        }
      `}</style>
    </section>
  );
};

export default LetsGetStartedSection;
